import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut } from "react-feather";
import { useAuth } from "../contexts/AuthContext";

const Navbar = () => {
  const location = useLocation();
  const [scrolling, setScrolling] = useState(false);
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const { user, isAuthenticated, getUsername, signOut } = useAuth();

  const isActive = (path) => location.pathname === path;

  // Debug logging
  /* console.log("Navbar - Auth state:", {
    user,
    isAuthenticated,
    username: getUsername(),
  }); */

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolling(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest(".user-menu")) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [userMenuOpen]);

  const navLinks = [
    { name: "Home", link: "/" },
    { name: "Services", link: "/services" },
    { name: "Products", link: "/products" },
    { name: "About Us", link: "/about" },
  ];

  return (
    <nav
      className={`fixed w-full z-[100] transition-all duration-500 ease-in-out px-6 lg:px-12 py-4 ${
        scrolling ?
          "bg-[#0B0F19]/80 backdrop-blur-lg border-b border-white/10 py-3"
        : "bg-transparent py-5"
      }`}>
      <div className="max-w-[1400px] mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="relative">
            <img
              src="/logo.png"
              className="w-12 h-12 md:w-14 md:h-14 transition-transform duration-300 group-hover:scale-110"
              alt="Logo"
            />
            {scrolling && (
              <div className="absolute inset-0 bg-[#00BCF0]/20 blur-xl rounded-full -z-10" />
            )}
          </div>
          <span className="text-white font-black text-xl md:text-2xl tracking-tighter uppercase italic">
            Connected <span className="text-[#00BCF0]">Group</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <ul className="flex items-center gap-8">
            {navLinks.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.link}
                  className={`text-sm uppercase tracking-widest font-bold transition-all duration-300 hover:text-[#00BCF0] ${
                    isActive(link.link) ? "text-[#00BCF0]" : "text-gray-300"
                  }`}>
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>

          <Link to="/contact#contact-form">
            <button className="relative px-6 py-2.5 bg-gradient-to-r from-[#007BFF] to-[#05DFAD] text-white font-bold rounded-lg overflow-hidden group">
              <span className="relative z-10 uppercase text-xs tracking-widest">
                Contact Us
              </span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
          </Link>

          {isAuthenticated && (
            <div className="relative user-menu">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg hover:bg-white/20 transition-all duration-300">
                <div className="w-8 h-8 bg-gradient-to-br from-[#00BCF0] to-[#008BB3] rounded-full flex items-center justify-center">
                  <User size={16} className="text-white" />
                </div>
                <span className="text-white text-sm font-semibold">
                  {getUsername()}
                </span>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-[#0B0F19] border border-white/10 rounded-lg shadow-xl overflow-hidden">
                  <div className="p-3 border-b border-white/10">
                    <p className="text-white text-sm font-semibold">
                      {getUsername()}
                    </p>
                    <p className="text-gray-400 text-xs">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-3 flex items-center gap-2 text-red-400 hover:bg-red-400/10 transition-colors">
                    <LogOut size={16} />
                    <span className="text-sm">Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          onClick={() => setOpen(!open)}
          className="md:hidden text-white cursor-pointer p-2 hover:bg-white/5 rounded-lg transition-colors">
          {open ?
            <X size={28} />
          : <Menu size={28} />}
        </div>
      </div>

      <div
        className={`
        fixed inset-x-0 top-[72px] bg-[#0B0F19] border-b border-white/10 p-8 flex flex-col gap-6 md:hidden transition-all duration-500 ease-in-out
        ${open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-10 pointer-events-none"}
      `}>
        {navLinks.map((link) => (
          <Link
            key={link.name}
            to={link.link}
            onClick={() => setOpen(false)}
            className={`text-2xl font-black uppercase italic ${
              isActive(link.link) ? "text-[#00BCF0]" : "text-white"
            }`}>
            {link.name}
          </Link>
        ))}
        <Link to="/contact#contact-form" onClick={() => setOpen(false)}>
          <button className="w-full py-4 bg-gradient-to-r from-[#007BFF] to-[#05DFAD] text-white font-bold rounded-xl uppercase tracking-widest">
            Contact Us
          </button>
        </Link>
      </div>
    </nav>
  );
};

export default Navbar;
